import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { productsData } from "../ProductsData";
import { CartContext } from "../components/CartContext";
import { useWishlist } from "../components/WishlistContext";
import ProductsList from "../components/productList";

const decodeAndCapitalize = (param) => {
    if (!param) return null;
    let decoded = decodeURIComponent(param);
    decoded = decoded.replace(/-/g, " ");
    return decoded
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
};

export default function ProductDetail() {
    const params = useParams();
    const category = decodeAndCapitalize(params.category);
    const subcategory = decodeAndCapitalize(params.subcategory);
    const id = params.id;

    const { addToCart, cartItems } = useContext(CartContext);
    const { toggleWishlist, wishlistItems } = useWishlist();

    const [alert, setAlert] = useState({ visible: false, message: "", type: "" });
    const [quantity, setQuantity] = useState(1);

    const imageRef = useRef(null);
    const [isZoomed, setIsZoomed] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const zoomLevel = 2.5;

    useEffect(() => window.scrollTo(0, 0), [id]);

    const product = subcategory
        ? productsData[category]?.[subcategory]?.[id - 1]
        : productsData[category]?.[id - 1];

    if (!product)
        return (
            <div className="container py-5 text-center">
                <p className="text-danger">Product not found.</p>
            </div>
        );

    const discountPercent =
        product.oldPrice && product.price
            ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100)
            : null;

    const productWithInfo = { ...product, category, subcategory };

    // ✅ Derive wishlist & cart status dynamically
    const isWishlisted = wishlistItems.some(
        (item) =>
            item.id === product.id &&
            item.category === category &&
            (item.subcategory || null) === (subcategory || null)
    );

    const isInCart = cartItems.some(
        (item) =>
            item.id === product.id &&
            item.category === category &&
            (item.subcategory || null) === (subcategory || null)
    );

    const handleCart = () => {
        addToCart({ ...productWithInfo, quantity });
        setAlert({ visible: true, message: `${product.name.slice(0, 30)} added to cart!`, type: "success" });
        setTimeout(() => setAlert({ visible: false, message: "", type: "" }), 3000);
    };

    const handleMouseMove = (e) => {
        if (!imageRef.current) return;
        const { left, top, width, height } = imageRef.current.getBoundingClientRect();
        setMousePos({ x: ((e.clientX - left) / width) * 100, y: ((e.clientY - top) / height) * 100 });
    };

    return (
        <div className="container">
            {alert.visible && (
                <div
                    className={`bg-primary text-white alert alert-${alert.type} alert-dismissible fade show fixed-top mt-3 mx-auto`}
                    role="alert"
                    style={{ zIndex: 1050, width: 'fit-content', minWidth: '300px' }}
                >
                    <i className="fa-solid fa-check-circle me-2"></i>
                    {alert.message}
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setAlert({ visible: false, message: "", type: "" })}
                    ></button>
                </div>
            )}
            <nav aria-label="breadcrumb" className="mt-4 mb-5">
                <ol className="breadcrumb">

                    {/* 1. Home Link (Always linked) */}
                    <li className="breadcrumb-item">
                        <Link to="/" className="text-dark text-decoration-none">
                            <i className="fas fa-home me-2"></i> Home
                        </Link>
                    </li>

                    {/* 2. Category Link (Linked if it's not the final step) */}
                    {category && (
                        <li className={`breadcrumb-item ${!subcategory ? 'active' : ''}`}
                            aria-current={!subcategory ? 'page' : undefined}>

                            {/* If a subcategory exists, the category is a link. Otherwise, it is the product name/category page itself. */}
                            {subcategory ? (
                                <Link to={`/category/${params.category}`} className="text-dark text-decoration-none">
                                    <i className="fas fa-shopping-cart me-2"></i> {category}
                                </Link>
                            ) : (
                                // Category is the final navigation step before the product detail
                                <i className="fas fa-shopping-cart me-2"></i> 
                            )}
                        </li>
                    )}

                    {/* 3. Subcategory Link (Linked if it's not the final step) */}
                    {subcategory && (
                        <li className={`breadcrumb-item ${!id ? 'active' : ''}`}
                            aria-current={!id ? 'page' : undefined}>

                            {/* If we are on the product detail page, the subcategory is a link */}
                            <Link to={`/category/${params.category}/${params.subcategory}`} className="text-dark text-decoration-none">
                                <i className="far fa-folder me-2"></i> {subcategory}
                            </Link>
                        </li>
                    )}

                    {/* 4. Product Name (Final step, always unlinked and active) */}
                    {product && (
                        <li className="breadcrumb-item active text-truncate" aria-current="page">
                            <i className="far fa-folder me-2"></i> {product.name}
                        </li>
                    )}
                </ol>
            </nav>
            <div className="row g-5">
                {/* LEFT IMAGE */}
                <div className="col-md-6">
                    <div
                        ref={imageRef}
                        className="position-relative overflow-hidden rounded-4 shadow-sm"
                        style={{ cursor: "zoom-in" }}
                        onMouseEnter={() => setIsZoomed(true)}
                        onMouseLeave={() => setIsZoomed(false)}
                        onMouseMove={handleMouseMove}
                    >
                        <img
                            src={product.image}
                            alt={product.name}
                            className="img-fluid rounded-3"
                            style={{ objectFit: "contain", maxHeight: "500px", width: "100%" }}
                        />
                        {isZoomed && (
                            <div
                                className="position-absolute top-0 start-0 w-100 h-100"
                                style={{
                                    backgroundImage: `url(${product.image})`,
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: `${mousePos.x}% ${mousePos.y}%`,
                                    backgroundSize: `${zoomLevel * 100}%`,
                                    transition: "opacity 0.2s ease",
                                    pointerEvents: "none",
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* RIGHT INFO */}
                <div className="col-md-6">
                    <p className="text-muted small text-uppercase">
                        {category} {subcategory && `/ ${subcategory}`}
                    </p>
                    <h2 className="fw-bold mb-3">{product.name}</h2>

                    <div className="mb-3">
                        <span className="fs-3 fw-bold me-3">${product.price.toFixed(2)}</span>
                        {product.oldPrice && (
                            <>
                                <del className="text-muted me-2">${product.oldPrice.toFixed(2)}</del>
                                {discountPercent && <span className="badge bg-danger">{discountPercent}% OFF</span>}
                            </>
                        )}
                    </div>

                    <p className="text-muted mb-4">{product.description.slice(0, 500)}...</p>

                    <div className="d-flex align-items-center gap-3 mb-4">
                        <button className="btn btn-outline-dark" onClick={() => quantity > 1 && setQuantity(quantity - 1)}>
                            <i className="fa-solid fa-minus"></i>
                        </button>
                        <span className="fw-bold">{quantity}</span>
                        <button className="btn btn-outline-dark" onClick={() => setQuantity(quantity + 1)}>
                            <i className="fa-solid fa-plus"></i>
                        </button>
                    </div>

                    <div className="d-flex gap-3">
                        {/* ADD / VIEW CART */}
                        {isInCart ? (
                            <Link to="/cart" className="btn btn-warning text-dark px-4">
                                <i className="fa-solid fa-check me-2"></i> VIEW CART
                            </Link>
                        ) : (
                            <button className="btn btn-dark px-4" onClick={handleCart}>
                                <i className="fa-solid fa-cart-shopping me-2"></i> ADD TO CART
                            </button>
                        )}

                        {/* WISHLIST TOGGLE */}
                        <button
                            className={`btn ${isWishlisted ? "btn-danger" : "btn-outline-dark"} px-4`}
                            onClick={() => toggleWishlist(productWithInfo)}
                        >
                            <i className={`fa-heart me-2 ${isWishlisted ? "fa-solid" : "fa-regular"}`}></i>
                            {isWishlisted ? "REMOVE FROM WISHLIST" : "ADD TO WISHLIST"}
                        </button>
                    </div>
                </div>
            </div>

            <hr className="my-5" />
            <h3 className="mb-3">Full Description</h3>
            <p>{product.description}</p>

            <h3 className="mt-5 mb-3">Related Products</h3>
            <ProductsList category={category} subcategory={subcategory} />
        </div>
    );
}
